import React, { useState, useEffect, useContext } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    List,
    ListItem,
    Button,
    Link as MuiLink,
    Stack,
    Dialog,
    DialogContent,
    IconButton,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    getOffsetRecord,
    getRecord,
    listRevisions,
    RecordListItem,
    getOriginal,
    createRevision,
} from '../api/records';
import LoadingPage from '../components/Loading';
import { Select, MenuItem } from '@mui/material';
import { fetchComments, Comment as CommentType } from '../api/comments';
import { listUsers, AppUser } from '../api/users';
import { AuthContext } from '../context/AuthContext';
import CommentThread from '../components/CommentThread';
import CommentBox from '../components/CommentBox';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MetadataCard from '../components/MetadataCard';
import remarkHashLinks from '../markdown/remarkHashLinks';
import { markdownLinkComponents } from '../markdown/markdownComponents';

type ReturnState = {
    returnPath?: string;
};

type Attachment = {
    id: string;
    path: string;
    hash: string;
};

type Record = {
    id: string;
    triplet_id: string;
    title: string;
    content: string;
    created_at: string;
    record_time: string;
    user_email: string;
    attachments: Attachment[];
    draft: boolean;
    revision_of: string;
    revision_group: string;
};

const RecordDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [record, setRecord] = useState<Record | null>(null);
    const [originalRecord, setOriginalRecord] = useState<Record | null>(null);
    const [revisions, setRevisions] = useState<RecordListItem[]>([]);
    const [nextRecord, setNextRecord] = useState<Record | null>(null);
    const [prevRecord, setPrevRecord] = useState<Record | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
    const [comments, setComments] = useState<CommentType[]>([]);
    const [allUsers, setAllUsers] = useState<AppUser[]>([]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        listUsers()
            .then(setAllUsers)
            .catch((err) => {
                console.error('Failed to fetch users for mentions', err);
            });
    }, []);

    const { email } = useContext(AuthContext);

    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as ReturnState;
    const returnPath = state?.returnPath ?? '/';

    const isImage = (filename: string): boolean => {
        return /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(filename);
    };

    // download helper: fetch as blob and trigger browser download
    const handleDownload = (backendUrl: string) => {
        const a = document.createElement('a');
        a.href = backendUrl;
        // no target or download attr; browser will follow redirect and stream file
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    // scroll to the top of the page - quick fix I guess for preserved home page scroll position
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        (async () => {
            if (!id) {
                setError('No record ID in URL');
                setLoading(false);
                return;
            }

            try {
                const data = await getRecord(id);

                if (data.triplet_id && data.triplet_id !== id) {
                    navigate(`/records/${data.triplet_id}`, {
                        replace: true,
                        state,
                    });
                }

                setRecord(data);
                const loadedComments = await fetchComments(data.revision_group);
                setComments(loadedComments);
                setPrevRecord(await getOffsetRecord(data.id, -1));
                setNextRecord(await getOffsetRecord(data.id, 1));

                setRevisions((await listRevisions(data.revision_group)).items);
                setOriginalRecord(await getOriginal(data.id));
            } catch (e: unknown) {
                console.error('API error:', e);
                if (e && typeof e === 'object' && 'response' in e) {
                    const err = e as {
                        response?: { data?: { detail?: string } };
                        message?: string;
                    };
                    setError(
                        err.response?.data?.detail ||
                            err.message ||
                            'Unknown error'
                    );
                } else {
                    setError('Unknown error');
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    if (loading) return <LoadingPage />;
    if (error) return <Typography color="error">{error}</Typography>;
    if (!record) return null;
    if (!originalRecord) return null;

    const isoUtc = record.created_at.endsWith('Z')
        ? record.created_at
        : record.created_at + 'Z';
    const when = new Date(isoUtc).toLocaleString();

    const isoUtc_rd = record.record_time.endsWith('Z')
        ? record.record_time
        : record.record_time + 'Z';
    const when_rd = new Date(isoUtc_rd).toLocaleString();

    const isoUtc_original = originalRecord.created_at.endsWith('Z')
        ? originalRecord.created_at
        : originalRecord.created_at + 'Z';
    const when_original = new Date(isoUtc_original).toLocaleString();

    const isoUtc_rd_original = originalRecord.record_time.endsWith('Z')
        ? record.record_time
        : record.record_time + 'Z';
    const when_rd_original = new Date(isoUtc_rd_original).toLocaleString();

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box
                display="flex"
                flexWrap={isMobile ? 'wrap' : 'nowrap'}
                gap={2}
                sx={{ mb: 2 }}
            >
                <Button
                    variant="outlined"
                    onClick={() => navigate(returnPath)}
                    sx={{ px: 1.1, pr: 1.5, minWidth: 'auto' }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ChevronLeftIcon
                            sx={{ fontSize: 20, mr: isMobile ? 0 : 0.25 }}
                        />
                        {!isMobile && 'Back'}
                    </Box>
                </Button>

                <Button
                    variant="outlined"
                    disabled={prevRecord == null}
                    onClick={() =>
                        navigate('/records/' + prevRecord?.triplet_id)
                    }
                >
                    {isMobile ? 'Prev' : 'Previous Record'}
                </Button>
                <Button
                    variant="outlined"
                    disabled={nextRecord == null}
                    onClick={() =>
                        navigate('/records/' + nextRecord?.triplet_id)
                    }
                >
                    {isMobile ? 'Next' : 'Next Record'}
                </Button>
                <Select
                    key={id}
                    defaultValue={id}
                    onChange={(event) => {
                        navigate('/records/' + event.target.value);
                    }}
                >
                    {revisions.map((rec, index) => {
                        const isoCreatedAt = rec.created_at.endsWith('Z')
                            ? rec.created_at
                            : rec.created_at + 'Z';
                        const localDate = new Date(isoCreatedAt);

                        const timeStr = localDate.toLocaleTimeString(
                            undefined,
                            {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false,
                            }
                        );

                        const dateStr = localDate.toLocaleDateString(
                            undefined,
                            {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                            }
                        );

                        return (
                            <MenuItem
                                key={rec.triplet_id}
                                value={rec.triplet_id}
                            >
                                {`REV${index + 1} (${timeStr} on ${dateStr})`}
                            </MenuItem>
                        );
                    })}
                </Select>
                <Button
                    variant="outlined"
                    onClick={async () => {
                        // create draft duplicate of this record, then redirect.
                        const revision = await createRevision(record.id);
                        console.log(
                            'redirecting to',
                            `/record-editor/${revision.triplet_id}`
                        );
                        navigate(`/record-editor/${revision.triplet_id}`);
                    }}
                >
                    Create Revision
                </Button>
            </Box>
            <MetadataCard revisionGroupId={record.revision_group} />
            {/* Header – full-width title and metadata */}
            <Stack spacing={1} sx={{ mb: 4 }}>
                {/* Title Card */}
                <Box
                    sx={{
                        bgcolor: 'grey.400',
                        borderRadius: 2,
                        px: 3,
                        py: 2,
                        width: '100%',
                        boxShadow: 1,
                    }}
                >
                    <Typography variant="h4" sx={{ fontWeight: 600, m: 0 }}>
                        {record.title}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 2,
                    }}
                >
                    <Typography variant="subtitle2">
                        By:{' '}
                        {isMobile
                            ? record.user_email.replace(
                                  /@valaratomics\.com$/,
                                  ''
                              )
                            : record.user_email}
                    </Typography>
                    <Typography variant="subtitle2">
                        Record Time: {when_rd}
                    </Typography>
                    <Typography variant="subtitle2">Logged: {when}</Typography>
                </Box>

                {originalRecord.id != record.id && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 2,
                        }}
                    >
                        <Typography variant="subtitle2" gutterBottom>
                            Original by:{' '}
                            {isMobile
                                ? originalRecord.user_email.replace(
                                      /@valaratomics\.com$/,
                                      ''
                                  )
                                : originalRecord.user_email}
                        </Typography>
                        <Typography variant="subtitle2" gutterBottom>
                            Original Record Time: {when_rd_original} &nbsp;
                        </Typography>
                        <Typography variant="subtitle2" gutterBottom>
                            Original Logged: {when_original}
                        </Typography>
                    </Box>
                )}
            </Stack>

            {/* content stack */}
            <Stack
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 2,
                    width: '100%',
                    //outline: '2px solid',
                    //outlineColor: 'primary.main',
                    // or a specific color
                }}
            >
                {/* permanent record stack */}
                <Stack
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: {
                            xs: '100%', // 100% width on extra-small and small screens
                            sm: '80%', // 80% width on medium screens and larger
                        },
                    }}
                >
                    <Stack>
                        <Box>
                            <Box>
                                <ReactMarkdown
                                    remarkPlugins={[
                                        remarkGfm,
                                        [remarkHashLinks, '/records/'],
                                    ]}
                                    components={markdownLinkComponents}
                                >
                                    {record.content}
                                </ReactMarkdown>
                            </Box>
                            <Box>
                                {record.attachments.length > 0 && (
                                    <>
                                        <Typography variant="h6" sx={{ mt: 4 }}>
                                            Attachments
                                        </Typography>
                                        <List>
                                            {record.attachments.map(
                                                (att, index) => {
                                                    const fname =
                                                        att.path
                                                            .split('/')
                                                            .pop() || att.id;
                                                    const backendUrl = `/api/records/attachment/${att.id}?draft=false`;
                                                    const rowBg =
                                                        index % 2 === 0
                                                            ? 'grey.100'
                                                            : 'white'; // alternate colors

                                                    return (
                                                        <ListItem
                                                            key={att.id}
                                                            disableGutters
                                                            sx={{
                                                                bgcolor: rowBg,
                                                                px: 2, // optional horizontal padding
                                                                py: 1, // optional vertical padding
                                                            }}
                                                        >
                                                            <MuiLink
                                                                component="button"
                                                                variant="body2"
                                                                underline="hover"
                                                                onClick={() =>
                                                                    handleDownload(
                                                                        backendUrl
                                                                    )
                                                                }
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                }}
                                                            >
                                                                {fname}
                                                            </MuiLink>
                                                        </ListItem>
                                                    );
                                                }
                                            )}
                                        </List>
                                    </>
                                )}
                            </Box>
                        </Box>
                    </Stack>
                    <Stack>
                        {record.attachments.some((att) =>
                            isImage(att.path)
                        ) && (
                            <>
                                <Typography variant="h6" sx={{ mt: 4 }}>
                                    Image Preview
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 2,
                                        mt: 2,
                                    }}
                                >
                                    {record.attachments.map((att) => {
                                        const isImg = isImage(att.path);
                                        if (!isImg) return null;

                                        const previewUrl = `/api/records/attachment/${att.id}?draft=false`;
                                        const altText =
                                            att.path.split('/').pop() || att.id;

                                        return (
                                            <Box
                                                key={att.id}
                                                component="img"
                                                src={previewUrl}
                                                alt={altText}
                                                onClick={() =>
                                                    setSelectedUrl(previewUrl)
                                                }
                                                sx={{
                                                    maxWidth: 200,
                                                    maxHeight: 200,
                                                    objectFit: 'contain',
                                                    borderRadius: 1,
                                                    boxShadow: 1,
                                                    cursor: 'pointer',
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            </>
                        )}
                    </Stack>
                </Stack>
            </Stack>
            <Dialog
                open={!!selectedUrl}
                onClose={() => setSelectedUrl(null)}
                maxWidth="xl"
            >
                <DialogContent
                    sx={{ p: 0, overflow: 'hidden', backgroundColor: 'black' }}
                >
                    <Box sx={{ position: 'relative' }}>
                        {/* Close “X” button */}
                        <IconButton
                            onClick={() => setSelectedUrl(null)}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                color: 'white',
                                backgroundColor: 'rgba(0,0,0,0.3)',
                                '&:hover': {
                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>

                        {/* Image always scaled to fit, no scrollbars */}
                        <Box
                            component="img"
                            src={selectedUrl || ''}
                            alt="Full size preview"
                            sx={{
                                display: 'block',
                                maxWidth: '100vw',
                                maxHeight: '100vh',
                                objectFit: 'contain',
                                margin: 'auto',
                            }}
                        />
                    </Box>
                </DialogContent>
            </Dialog>
            {/* Comment box */}
            <CommentBox
                revisionGroupId={record.revision_group}
                allUsers={allUsers}
                onNewComment={(newComment) =>
                    setComments((prev) => [...prev, newComment])
                }
            />
            <Box
                sx={{
                    mt: 4,
                    width: {
                        xs: '100%',
                        sm: '80%',
                    },
                }}
            >
                {email && comments.length > 0 ? (
                    <CommentThread
                        comments={comments}
                        setComments={setComments}
                        currentUserEmail={email}
                        allUsers={allUsers}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No comments yet.
                    </Typography>
                )}
            </Box>
        </Container>
    );
};

export default RecordDetailPage;
